import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    makeStyles,
    Button,
    TextField,
    Collapse,
    Tooltip,
    IconButton,
} from '@material-ui/core';

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";

import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from '@material-ui/icons/Add';


const useStyles = makeStyles(() => ({

    listContainer: {
        maxHeight: "400px",
        overflowY: "auto",
    },



    listTitle: {
        fontWeight: 600,
        fontSize: "0.9rem"
    },

    listDescription: {
        fontSize: "0.8rem",
        color: "#555"
    },

    addButtonWrapper: {
        marginTop: "0.75rem",
        marginLeft: "0.75rem",   // 👈 added left spacing
    },

    addButton: {
        textTransform: "none",
        padding: "2px 4px",
        fontSize: "0.85rem",
        fontWeight: 500,
        transition: "all 0.2s ease",
        borderRadius: "4px",
      
        '&:hover': {
          backgroundColor: "#f3f3f3",
          transform: "translateY(-1px)",
        }

      },
      

    addForm: {
        border: "1px solid #ddd",
        borderRadius: "8px",
        paddingTop: "0.6rem",
        paddingBottom: "0.6rem",
        paddingLeft: "1.8rem",      // 👈 increased
        paddingRight: "1.8rem",     // 👈 increased
        marginTop: "0.5rem",
        background: "#fafafa",
    },


    input: {
        fontSize: "0.8rem",
        fontWeight: 400,
        '& input': {
            fontWeight: 400,
            fontSize: "0.8rem",
        },
        '& textarea': {
            fontWeight: 400,
            fontSize: "0.8rem",
        }
    },



    fieldSpacing: {
        marginTop: "0.3rem"
    },

    saveButtonContainer: {
        display: "flex",
        justifyContent: "flex-end",
    },

    saveButton: {
        textTransform: "none",
        padding: "2px 6px",
        minWidth: "auto",
        fontSize: "0.75rem",     // smaller button text
        fontWeight: 700,
        borderRadius: "4px",
        transition: "all 0.2s ease",
        '&:hover': {
            backgroundColor: "#f0f0f0",
            transform: "translateY(-1px)"
        }
    },

    cancelButton: {
        textTransform: "none",
        padding: "2px 6px",
        minWidth: "auto",
        fontSize: "0.75rem",
        fontWeight: 400,
        marginRight: "8px",
        color: "#777",
        transition: "all 0.2s ease",
        '&:hover': {
          backgroundColor: "#f3f3f3",
        }
      },
      

    listItem: {
        padding: "0.5rem 0.75rem",
        paddingRight: "70px", // space for icons
        borderBottom: "1px solid #eee",
        borderRadius: "6px",
        position: "relative",
        '&:hover': {
            background: "#f5f5f5",
        }
    },

    actionIconButton: {
        padding: 3,                // smaller clickable area
    },

    actionIcon: {
        fontSize: "0.9rem",        // smaller than default small
        color: "#666",
        transition: "all 0.15s ease",
    },

    actionIconHover: {
        '&:hover': {
            backgroundColor: "#f2f2f2",
        }
    },
    helperText: {
        fontSize: "0.7rem",
        fontWeight: 400,
        marginTop: 2
    },
    nameInput: {
        fontSize: "0.82rem",
        fontWeight: 500,          // 👈 slightly bolder
        '& input': {
            fontWeight: 500,
            fontSize: "0.82rem",
        },
    },

    descriptionInput: {
        fontSize: "0.8rem",
        fontWeight: 400,
        '& textarea': {
            fontWeight: 400,
            fontSize: "0.8rem",
        },
    },

    dragHandle: {
        display: "flex",
        alignItems: "center",
        marginRight: "8px",
        cursor: "grab",
        color: "#aaa",
    },

    listItemRow: {
        display: "flex",
        alignItems: "flex-start",
    },

    emptyState: {
        padding: "1rem",
        textAlign: "center",
        fontSize: "0.85rem",
        color: "#888",
        fontWeight: 400,
    },

}));

function AIGeneratedItemsList({ items, setItems, entity, validationErrors = [], validations={}, placeholders
}) {
    const classes = useStyles();

    const [showForm, setShowForm] = useState(false);
    const [newItem, setNewItem] = useState({
        name: "",
        description: ""
    });
    const [editingIndex, setEditingIndex] = useState(null);
    const [editItem, setEditItem] = useState({ name: "", description: "" });


    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    const [editErrors, setEditErrors] = useState({});

    const namePlaceholder = placeholders?.name || "Name"
    const descriptionPlaceholder = placeholders?.description || "Describe what this functionality should do in detail"

    const nameInputRef = useRef(null);
    const editInputRefs = useRef({});

    const descriptionTextareaRowsCount = entity === "Table" ? 5 : 3;

    const listContainerRef = useRef(null);

    const nameRegex = /^[A-Za-z0-9_]+$/;

    const errorMap = {};

    validationErrors.forEach(err => {
        errorMap[err.key] = err.message;
    });


    const handleDelete = (index) => {
        const updated = items.filter((_, i) => i !== index);
        setItems(updated);
    };

    const handleEdit = (index) => {
        // Close add form if open
        if (showForm) {
          setShowForm(false);
          setErrors({});
          setNewItem({ name: "", description: "" });
        }
      
        // Open selected edit form (even if another edit is open)
        setEditingIndex(index);
        setEditItem(items[index]);
        setEditErrors({});
      };

    const handleUpdate = () => {
        const newErrors = validateItem(editItem);
        setEditErrors(newErrors);
      
        if (Object.keys(newErrors).length > 0) return;
      
        const updated = [...items];
        updated[editingIndex] = editItem;

        const duplicateErrors = runEntityValidation(updated);
        const newIndex = updated.length - 1;

        const currentError = duplicateErrors[0]
      
        if (currentError) {
        setEditErrors({ name: currentError.message });
          return;
        }
      
        setItems(updated);
        setEditingIndex(null);
        setEditErrors({});
      };
      
      

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        // Disable reorder while adding or editing
        if (showForm || editingIndex !== null) return;

        const reordered = Array.from(items);
        const [movedItem] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, movedItem);

        setItems(reordered); // Persist new order
    };

    const handleDragUpdate = (update) => {
        if (!update.destination) return;
        if (!listContainerRef.current) return;
      
        const container = listContainerRef.current;
        const rect = container.getBoundingClientRect();
      
        const mouseY = update.client?.selection?.y;
        if (!mouseY) return;
      
        const threshold = 30; // distance from edge
        const slowSpeed = 3;  // 👈 smaller = slower scroll
      
        // Scroll Down
        if (mouseY > rect.bottom - threshold) {
          container.scrollTop += slowSpeed;
        }
      
        // Scroll Up
        if (mouseY < rect.top + threshold) {
          container.scrollTop -= slowSpeed;
        }
      };
      

    

    useEffect(() => {
        if (showForm && nameInputRef.current) {
          setTimeout(() => {
            nameInputRef.current.focus();
          }, 250); // match Collapse timeout
        }
      }, [showForm]);

   
    useEffect(() => {
        if (editingIndex !== null) {
          requestAnimationFrame(() => {
            const input = editInputRefs.current[editingIndex];
            if (input) {
              input.focus();
            }
          });
        }
      }, [editingIndex]);
      


    const resetForm = () => {
        setShowForm(false);
        setNewItem({ name: "", description: "" });
        setErrors({});
        setTouched({});
    };

    const validateItem = (item) => {
        const newErrors = {};
      
        if (!item.name.trim()) {
          newErrors.name = "Name is required";
        } else if (!nameRegex.test(item.name)) {
          newErrors.name =
            "Only letters, numbers, underscore are allowed";
        }
      
        if (!item.description.trim()) {
          newErrors.description = "Description is required";
        }
      
        return newErrors;
      };

      const runEntityValidation = (updatedList) => {
        if (entity === "View" && validations["view"]) {
          return validations["view"](updatedList);
        }
      
        if (entity === "Transaction" && validations["transaction"]) {
          return validations["transaction"](updatedList);
        }
      
        if (entity === "Testsuite" && validations["testsuite"]) {
          return validations["testsuite"](updatedList);
        }

        if (entity === "Table" && validations["table"]) {
            return validations["table"](updatedList);
        }
        
      
        return [];
      };
      

      const handleSave = () => {
        const newErrors = validateItem(newItem);
        console.log("newErrors---", newErrors)
        setErrors(newErrors);
      
        if (Object.keys(newErrors).length > 0) return;
      
        const updatedItems = [
          ...items,
          {
            ...newItem,
            view_type: "individual"
          }
        ];


        const duplicateErrors = runEntityValidation(updatedItems);
        console.log("duplicateErrors---",duplicateErrors)
        const currentError = duplicateErrors[0]
      
        if (currentError) {
          setErrors({ name: currentError.message });
          return;
        }
      
        setItems(updatedItems);
        resetForm();
      };
      
      

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
    };

    const showError = (field) => touched[field] && errors[field];


    const handleAddClick = () => {
        // Close edit form if open
        if (editingIndex !== null) {
          setEditingIndex(null);
          setEditErrors({});
        }
      
        setShowForm(true);
      };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault(); // prevent newline
            handleSave();
        }
    };



    return (


        <Box>

            {/* Scrollable List ONLY */}
            <Box className={classes.listContainer} ref={listContainerRef}>

                {(!items || items.length === 0) && !showForm && (
                    <Box className={classes.emptyState}>
                        No functionalities generated
                    </Box>
                )}

                <DragDropContext 
                    onDragEnd={handleDragEnd}
                    onDragUpdate={handleDragUpdate}
                  
                >
                    <Droppable droppableId="ai-list">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>

                                {items?.map((item, index) => (
                                    <Draggable
                                        key={index.toString()}
                                        draggableId={index.toString()}
                                        index={index}
                                        isDragDisabled={showForm || editingIndex !== null}
                                    >
                                        {(provided) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                            >

                                                <Box position="relative">

                                                    {/* Normal View */}
                                                    <Collapse in={editingIndex !== index} timeout={250}>
                                                        <div
                                                            className={classes.listItem}
                                                        >
                                                            <div className={classes.listItemRow}>

                                                                {/* Drag Handle */}
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    className={classes.dragHandle}
                                                                >
                                                                    <DragIndicatorIcon fontSize="small" />
                                                                </div>

                                                                <div style={{ flex: 1 }}>

                                                                    {/* Icons Top Right */}
                                                                    <Box position="absolute" top={4} right={4} display="flex">

                                                                        <Tooltip title="Edit">
                                                                            <IconButton
                                                                                className={`${classes.actionIconButton} ${classes.actionIconHover}`}
                                                                                size="small"
                                                                                onClick={() => handleEdit(index)}
                                                                            >
                                                                                <EditIcon className={classes.actionIcon} />
                                                                            </IconButton>
                                                                        </Tooltip>

                                                                        <Tooltip title="Delete">
                                                                            <IconButton
                                                                                className={`${classes.actionIconButton} ${classes.actionIconHover}`}
                                                                                size="small"
                                                                                onClick={() => handleDelete(index)}
                                                                            >
                                                                                <DeleteIcon className={classes.actionIcon} />
                                                                            </IconButton>
                                                                        </Tooltip>

                                                                    </Box>

                                                                    <div className={classes.listTitle}>{item.name}</div>
                                                                    <div className={classes.listDescription}>
                                                                        {item.description}
                                                                    </div>

                                                                    {errorMap[`${item.name}_${index}`] && (
                                                                        <div
                                                                            style={{
                                                                                fontSize: "0.7rem",
                                                                                color: "#d32f2f",
                                                                                marginTop: "2px"
                                                                            }}
                                                                        >
                                                                            {errorMap[`${item.name}_${index}`]}
                                                                        </div>
                                                                    )}

                                                                </div>

                                                            </div>
                                                        </div>
                                                    </Collapse>

                                                    {/* Edit Mode (UNCHANGED) */}
                                                    <Collapse 
                                                        in={editingIndex === index}
                                                        timeout={250}
                                                       
                                                    >
                                                        <Box
                                                            className={classes.addForm}
                                                        >
                                                            <TextField
                                                                fullWidth
                                                                value={editItem.name}
                                                                onChange={(e) =>
                                                                  setEditItem({ ...editItem, name: e.target.value })
                                                                }
                                                                error={Boolean(editErrors.name)}
                                                                helperText={editErrors.name || ""}
                                                               
                                                                inputRef={(el) => {
                                                                    if (el) {
                                                                      editInputRefs.current[index] = el;
                                                                    }
                                                                  }}

                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        handleUpdate();
                                                                    }
                                                                }}
                                                                placeholder={namePlaceholder}
                                                                InputProps={{
                                                                    disableUnderline: true,
                                                                    className: classes.nameInput
                                                                  }}
                                                                
                                                                FormHelperTextProps={{
                                                                    className: classes.helperText
                                                                }}

                                                            />

                                                            <TextField
                                                                fullWidth
                                                                multiline
                                                                rows={descriptionTextareaRowsCount}
                                                                value={editItem.description}
                                                                onChange={(e) =>
                                                                  setEditItem({ ...editItem, description: e.target.value })
                                                                }
                                                                error={Boolean(editErrors.description)}
                                                                helperText={editErrors.description || ""}
                                                                className={classes.fieldSpacing}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === "Enter" && !e.shiftKey) {
                                                                        e.preventDefault();
                                                                        handleUpdate();
                                                                    }
                                                                }}
                                                                placeholder={descriptionPlaceholder}
                                                                InputProps={{
                                                                    disableUnderline: true,
                                                                    className: classes.descriptionInput
                                                                }}
                                                                FormHelperTextProps={{
                                                                    className: classes.helperText
                                                                }}
                                                            />

                                                            <Box className={classes.saveButtonContainer}>
                                                                <Button
                                                                    className={classes.cancelButton}
                                                                    onClick={() => {
                                                                        setEditingIndex(null);
                                                                        setEditErrors({});
                                                                      }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                                <Button
                                                                    className={classes.saveButton}
                                                                    onClick={handleUpdate}
                                                                >
                                                                    Save
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </Collapse>

                                                </Box>

                                            </div>
                                        )}
                                    </Draggable>
                                ))}

                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                {/* Add Form (Outside List) */}
                <Collapse 
                    in={showForm}
                    timeout={300}
                    unmountOnExit
                    onEntered={() => {
                      if (!listContainerRef.current) return;
                  
                      const container = listContainerRef.current;
                  
                      container.scrollTo({
                        top: container.scrollHeight,
                        behavior: "smooth"
                      });
                  
                      if (nameInputRef.current) {
                        nameInputRef.current.focus();
                      }
                    }}
                >
                    <Box
                        className={classes.addForm}
                    >
                        {/* Name Field */}
                        <TextField
                            fullWidth
                            inputRef={nameInputRef}
                            placeholder={namePlaceholder}
                            value={newItem.name}
                            onChange={(e) => {
                                setNewItem({ ...newItem, name: e.target.value })

                              }}
                            onBlur={() => handleBlur("name")}
                            onKeyDown={handleKeyDown}
                            error={Boolean(errors.name)}
                            helperText={errors.name || ""}

                            InputProps={{
                                disableUnderline: true,
                                className: classes.nameInput
                            }}
                            FormHelperTextProps={{
                                className: classes.helperText
                            }}
                        />

                        {/* Description Field */}
                        <TextField
                            fullWidth
                            multiline
                            rows={descriptionTextareaRowsCount}
                            placeholder={descriptionPlaceholder}
                            value={newItem.description}
                            onChange={(e) =>
                                setNewItem({ ...newItem, description: e.target.value })
                            }
                            onBlur={() => handleBlur("description")}
                            onKeyDown={handleKeyDown}
                            error={Boolean(showError("description"))}
                            helperText={showError("description") ? errors.description : ""}
                            InputProps={{
                                disableUnderline: true,
                                className: classes.descriptionInput
                            }}
                            FormHelperTextProps={{
                                className: classes.helperText
                            }}
                            className={classes.fieldSpacing}
                        />


                        <Box className={classes.saveButtonContainer}>
                            <Button
                                className={classes.cancelButton}
                                onClick={() => resetForm()}
                            >
                                Cancel
                            </Button>
                            <Button
                                className={classes.saveButton}
                                onClick={handleSave}
                            >
                                Save
                            </Button>
                        </Box>
                    </Box>
                </Collapse>
            </Box>



            {/* Add Button (Outside List) */}
            {!showForm && (
                <Box className={classes.addButtonWrapper}>
                    <Button
                        className={classes.addButton}
                        startIcon={<AddIcon />}
                        onClick={handleAddClick}
                    >
                        {`Add New ${entity || "Item"}`}
                    </Button>
                </Box>
            )}

            

        </Box>

    );
}

export default AIGeneratedItemsList;
